import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { groupBy, omit, uniq } from 'lodash-es'
import { CommonService, DayOption } from './common.service'
import { DatePipe } from '@angular/common'
import { FormGroup } from '@angular/forms'
import { environment } from '@app/../config/environments/environment'
import { DetailsService } from '../pages/settings/services/details.service'
import { take } from 'rxjs'

/** A pass validity window short enough to be one visit, as the two lines a table cell draws it on. */
export interface CompactRange {
  /** The day it sits in - both days when it runs past midnight. */
  day: string
  /** The hours it covers there. */
  hours: string
}

/**
 * A validity window's end, as the day it actually covers.
 *
 * The portal spells "end of day" two ways: the vehicle, HME, pass and employee modals save 23:59:59
 * of the chosen day, while `buildTimeLimitationRange` ends a window at the NEXT day's 00:00
 * (setHours(24)). Printed raw, the second spelling reads as one day too many, and a date picker
 * loaded with it saves a pass a day longer than the one it was opened on.
 *
 * Only an end stamped exactly on midnight moves; every other end is an hour somebody actually
 * chose, and printing 11:59 for a window that ends at noon would be a lie.
 * @param end Window end
 * @returns The same instant, or the second before it when it lands on midnight
 */
export function windowEndForDisplay(end: Date): Date {
  return !end.getHours() && !end.getMinutes() && !end.getSeconds() ? new Date(end.valueOf() - 1000) : end
}

/**
 * The `range_allowed` an edit form should save for the days it shows.
 *
 * The pass drawer edits DATES only, and used to rebuild the range from them on every save as
 * 00:00 – 23:59:59. Saving a pass untouched therefore rewrote its window: a registration-desk pass
 * valid for fifteen minutes became valid all day (found 18/10/2026), and an end stored as the next
 * day's 00:00 moved to 23:59:59. So when the picked days are exactly the days the stored window
 * covers, the stored range goes back unchanged — hours and all — and only a real date change is
 * rebuilt from the picked days.
 * @param storedRange The pass's current `range_allowed` ("<start>-<end>" in epoch seconds), if any
 * @param startDate The start day shown in the form
 * @param endDate The end day shown in the form
 * @returns The range to save
 */
export function rangeForEditedDates(storedRange: string | null | undefined, startDate: Date | string, endDate: Date | string): string {
  const start = new Date(startDate)
  start.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(23, 59, 59, 999)

  const [from, to] = String(storedRange ?? '')
    .split('-')
    .map((s) => Number(s))
  if (Number.isFinite(from) && Number.isFinite(to) && from > 0 && to > 0) {
    const storedStart = new Date(from * 1000)
    const storedEnd = windowEndForDisplay(new Date(to * 1000))
    if (storedStart.toDateString() === start.toDateString() && storedEnd.toDateString() === end.toDateString()) return `${from}-${to}`
  }

  return `${Math.floor(start.valueOf() / 1000)}-${Math.floor(end.valueOf() / 1000)}`
}

interface RestrictionTimeInput {
  daysWeek: number[]
  end_time: Date
  start_time: Date
}

interface TimeLimitationDay {
  daysWeek: number
  end_time: string
  start_time: string
}

interface RawDayTime {
  start_time: string
  end_time: string
  day: number
  key?: string
}

interface ParsedTimeLimitation {
  start_time: string
  end_time: string
  day: number[]
}

interface RestrictionElement {
  hourFirstRange: string
  hourSecondRange: string
  daysWeek: (number | DayOption)[]
}

interface TimeRestriction {
  restrictionId: number
  daysWeek: DayOption[]
  hourFirstRange: Date
  hourSecondRange: Date
}

@Injectable({
  providedIn: 'root',
})
export class DateService {
  timezone: string
  private timezoneInitialized = false

  constructor(private translate: TranslateService, private commonService: CommonService, private datePipe: DatePipe, private detailsService: DetailsService) {
    // Don't fetch timezone in constructor - do it lazily when needed
  }

  private initializeTimezone() {
    if (!this.timezoneInitialized) {
      this.timezoneInitialized = true
      this.detailsService
        .getCurrentClientBy('time_zone')
        .pipe(take(1))
        .subscribe((cli: { time_zone: number }) => {
          this.timezone = `GMT${cli.time_zone > 0 ? '+' + cli.time_zone : cli.time_zone}`
        })
    }
  }

  startsIn(dateStr: string): string | null {
    const now = new Date()
    const targetDate = new Date(dateStr)
    const diff = targetDate.getTime() - now.getTime()

    if (diff < 0) {
      return null // The date is in the past.
    }

    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / 86400)
    if (days > 7) {
      return null // Beyond the next week
    }

    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const hoursFormatted = hours < 10 ? '0' + hours : hours.toString()
    const minutesFormatted = minutes < 10 ? '0' + minutes : minutes.toString()
    const secondsFormatted = seconds < 10 ? '0' + seconds : seconds.toString()

    return (days ? days + ' ' + this.translate.instant('COMMON.DATE.DAYS') + ', ' : '') + hoursFormatted + ':' + minutesFormatted + ':' + secondsFormatted
  }

  syncDates(dateDay: Date, dateTime: Date): Date {
    dateDay.setHours(dateTime.getHours())
    dateDay.setMinutes(dateTime.getMinutes())
    dateDay.setSeconds(0)

    dateTime.setDate(dateDay.getDate())
    dateTime.setMonth(dateDay.getMonth())
    dateTime.setFullYear(dateDay.getFullYear())
    dateTime.setSeconds(0)
    return dateDay
  }

  timeToDate(time: string) {
    const [hours, minutes] = time.split(':').map((e) => parseInt(e))
    const date = new Date()
    date.setHours(hours)
    date.setMinutes(minutes)
    return date
  }

  dateToTime(date: Date): string {
    const convertedDate = new Date(date)
    const hours = convertedDate.getHours().toString().padStart(2, '0')
    const minutes = convertedDate.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  formatDate(dateString: Date) {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)

    return `${day}/${month}/${year}`
  }

  dateToTimeWithOnlyNumbers(date: Date) {
    return [date.getHours(), date.getMinutes()]
      .map((e) => {
        return e < 10 ? '0' + e : e
      })
      .join('')
  }

  createTimeLimitationsList(data: RestrictionTimeInput[]): TimeLimitationDay[] {
    const days: TimeLimitationDay[] = []
    data.forEach((el) => {
      el.daysWeek.forEach((dayNumber) => {
        days.push({
          daysWeek: dayNumber,
          end_time: this.dateToTime(el.end_time),
          start_time: this.dateToTime(el.start_time),
        })
      })
    })

    return days
  }

  parseTimeLimitations(str: string): ParsedTimeLimitation[] {
    let result: { day_time_allowed?: RawDayTime[] }
    try {
      result = JSON.parse(str) as { day_time_allowed?: RawDayTime[] }
    } catch (_e) {
      return []
    }
    if (!result.day_time_allowed) {
      return []
    }

    result.day_time_allowed.map((el) => {
      el.key = el.start_time + el.end_time
    })
    const obj = groupBy(result.day_time_allowed, 'key')
    const results: ParsedTimeLimitation[] = []
    for (const key in obj) {
      const items = obj[key]
      const base = omit(items[0], 'key')
      results.push({ ...base, day: uniq(items.map((e) => e.day)) })
    }
    return results
  }

  /**
   * Get day hours allowed object
   * @param selectedRestrictions Selected restriction array contain day and hours range
   * @returns Object of {day : hours}
   */
  getDaysHoursAllowed(selectedRestrictions: RestrictionElement[]) {
    const obj: Record<number, string[]> = {}
    selectedRestrictions.forEach((element) => {
      // Validate that required time properties exist and are strings
      if (!element || typeof element.hourFirstRange !== 'string' || typeof element.hourSecondRange !== 'string' || !Array.isArray(element.daysWeek)) {
        console.warn('Invalid restriction element:', element)
        return
      }

      // If "Everyday" is selected (value == -1), replace with actual days
      if (element.daysWeek[0] === -1) {
        element.daysWeek = this.commonService.getDayList(0)
      }

      element.daysWeek.forEach((restric) => {
        // Determine the day value
        const dayValue: number = ((restric as DayOption).value !== undefined ? (restric as DayOption).value : restric) as number

        // Ensure dayValue is defined
        if (dayValue === undefined) {
          console.warn('dayValue is undefined for restric:', restric)
          return
        }

        const timeRange = `${element.hourFirstRange.replace(':', '')}-${element.hourSecondRange.replace(':', '')}`

        if (obj[dayValue]) {
          obj[dayValue].push(timeRange)
        } else {
          obj[dayValue] = [timeRange]
        }
      })
    })
    return obj
  }

  /**
   *
   * @param time_limitation Time limitation split if date convert from timestamp if time return time only
   * @returns
   */
  timeLimitationSplit(time_limitations: string, index: number, key: string, isTime?: boolean): string {
    if (time_limitations) {
      const obj = (JSON.parse(time_limitations) as Record<string, unknown>)[key]
      const split: string[] = isTime ? (obj as Record<string, string[]>)[Object.keys(obj as object)[0]][0].split('-') : (obj as string)?.split('-')
      let date: string | number

      if (isTime) {
        split[index] = [split[index].slice(0, 2), ':', split[index].slice(2, 4)].join('')
      } else {
        split[index] = (Number.parseInt(split[index]) * 1000).toString()
      }

      if (!isTime && index == 1) {
        const convertedDate = new Date(Number.parseInt(split[index]))
        convertedDate.setDate(new Date(Number.parseInt(split[index])).getDate() - 1)
        date = convertedDate.valueOf()
      } else {
        date = split[index]
      }
      return date as string
    }
    return ''
  }

  /**
   * Build and get restriction array by time  limitation
   * @param time_limitations Timelimitation Value
   * @param key
   * @returns
   */
  buildAndGetRestrictionsByTimeLimitation(time_limitations: string, key: string): TimeRestriction[] {
    const restrictions: TimeRestriction[] = []

    if (time_limitations && time_limitations.indexOf('hour') >= 0) {
      try {
        const parsedObj = JSON.parse(time_limitations) as Record<string, Record<string, string[]>>
        const obj = parsedObj[key]

        if (!obj) return restrictions

        // Create a map of time ranges to days they apply to
        const timeRangeMap = new Map<string, number[]>()

        // Collect all unique time ranges and which days they apply to
        Object.keys(obj).forEach((day) => {
          const dayNumber = parseInt(day)
          const timeRanges = obj[day]

          timeRanges.forEach((range: string) => {
            if (!timeRangeMap.has(range)) {
              timeRangeMap.set(range, [])
            }
            timeRangeMap.get(range)!.push(dayNumber)
          })
        })

        // Create a restriction for each unique time range
        let restrictionId = 0
        timeRangeMap.forEach((days, timeRange) => {
          restrictionId++

          const [startStr, endStr] = timeRange.split('-')
          const startTime = [startStr.slice(0, 2), ':', startStr.slice(2, 4)].join('')
          const endTime = [endStr.slice(0, 2), ':', endStr.slice(2, 4)].join('')

          // Set days of week - if all 7 days are included, use "every day"
          let daysWeek: DayOption[]
          if (days.length === 7 && [1, 2, 3, 4, 5, 6, 7].every((day) => days.includes(day))) {
            daysWeek = this.commonService.getDayList(0, true).filter((el) => el.value === -1)
          } else {
            daysWeek = this.commonService.getDayList(0, true).filter((el) => days.includes(el.value))
          }

          restrictions.push({
            restrictionId,
            daysWeek,
            hourFirstRange: this.timeToDate(startTime),
            hourSecondRange: this.timeToDate(endTime),
          })
        })
      } catch (error) {
        console.error('Error parsing time limitations:', error)
      }
    }

    return restrictions
  }

  /**
   * Set time to date
   * @param date Date
   * @param time Time
   */
  setTimeToDate(date: Date, time: string) {
    const [hours, minutes] = time.split(':').map((e) => parseInt(e))
    date.setHours(hours)
    date.setMinutes(minutes)
    return date
  }

  /**
   * Convert to local date
   * @param date Date value
   * @returns
   */
  convertToLocalDate(date: string | Date | null | undefined, isEndingDate?: boolean) {
    if (date) {
      try {
        // Create a proper Date object from the input (this is in UTC)
        let utcDate = new Date(date)

        // For ending dates, subtract 1 second to handle the 24:00:00 case
        // This must be done BEFORE timezone conversion
        if (isEndingDate) {
          utcDate = new Date(utcDate.getTime() - 1000)
        }

        // Use the existing dateByTimezone method to properly convert to the client's timezone
        // This method already handles the timezone conversion correctly
        const localDateString = this.dateByTimezone(utcDate, 'yyyy-MM-ddTHH:mm:ss.SSSZ')
        const localDate = new Date(localDateString ?? '')

        if (typeof date == 'string') return localDate
        return localDate.toISOString()
      } catch (_error) {
        return ''
      }
    }
    return ''
  }

  /**
   * Hours below which a validity window is one visit rather than a range of days.
   *
   * "Inside a day" would be 24; 20 leaves room for the minutes a window picks up on the way in - a
   * timezone shift, an early-entry grace - without an ordinary day-long pass collapsing into one date.
   */
  readonly compactPassRangeHours = 20

  /**
   * A validity window short enough to be a single visit, split into the two lines it is drawn on.
   *
   * Kept as parts rather than one string so the column can stack them: on one line a date followed by
   * two times is wider than every other cell in the table, and it was the width that made the hours
   * unreadable in the first place.
   *
   * Both parts carry bidi isolates. The date-only rendering straightens itself in an RTL cell by
   * printing its two ends backwards and letting the bidi algorithm swap them back - a trick that
   * works for two dates and for nothing else.
   *
   * `null` means "this window is not short" - the caller then keeps whatever date-only rendering it
   * has today, so nothing about an ordinary pass moves.
   * @param from Window start
   * @param to Window end
   * @returns The day and the hours it covers, or null when the window is not short
   */
  compactRangeParts(from: Date, to: Date): CompactRange | null {
    if (isNaN(from.valueOf()) || isNaN(to.valueOf())) return null

    const hours = (to.valueOf() - from.valueOf()) / 3600000
    if (hours <= 0 || hours >= this.compactPassRangeHours) return null

    // An end stamped on midnight belongs to the day that just ended - the same second
    // `convertToLocalDate(x, true)` takes off for the date-only rendering.
    const end = windowEndForDisplay(to)

    const day = (d: Date) => this.datePipe.transform(d, 'dd/MM/yyyy') ?? ''
    const time = (d: Date) => this.datePipe.transform(d, 'HH:mm') ?? ''
    const isolate = (s: string) => `\u2066${s}\u2069`

    return {
      // A window can run past midnight and still be one visit; it then needs both days, and saying so
      // on the day line is the one place it costs nothing.
      day: isolate(day(from) === day(end) ? day(from) : `${day(from)} - ${day(end)}`),
      hours: isolate(`${time(from)} - ${time(end)}`),
    }
  }

  /**
   * The same, for the two ends as they are stored on a row.
   * @param start Window start
   * @param end Window end
   * @returns The day and the hours it covers, or null when the window is not short
   */
  compactPassRange(start: string | Date | null | undefined, end: string | Date | null | undefined): CompactRange | null {
    if (!start || !end) return null
    return this.compactRangeParts(new Date(this.convertToLocalDate(start) as string | Date), new Date(this.convertToLocalDate(end) as string | Date))
  }

  /**
   * A window of a day or more whose ends fall mid-day, as the same two lines: its days, and under them
   * the hours it starts and stops.
   *
   * A vehicle replaced by its driver is closed at the minute of the change (YB-2124, QA 20/10/2026), so
   * its window ends at, say, 21:30 rather than at the end of a day. Printed as dates alone it reads as
   * still valid all day. `null` for a window that starts at 00:00 and ends at the end of a day - an
   * ordinary pass keeps its date-only rendering - and for one with no end.
   * @param from Window start
   * @param to Window end
   * @returns The days and the hours, or null when both ends are day bounds
   */
  timedRangeParts(from: Date, to: Date): CompactRange | null {
    if (isNaN(from.valueOf()) || isNaN(to.valueOf()) || to.valueOf() <= from.valueOf()) return null
    if (this.noDateProvided(to.toISOString())) return null

    const end = windowEndForDisplay(to)
    const startsOnDay = !from.getHours() && !from.getMinutes()
    const endsOnDay = end.getHours() === 23 && end.getMinutes() === 59
    if (startsOnDay && endsOnDay) return null

    const day = (d: Date) => this.datePipe.transform(d, 'dd/MM/yyyy') ?? ''
    const time = (d: Date) => this.datePipe.transform(d, 'HH:mm') ?? ''
    const isolate = (s: string) => `⁦${s}⁩`

    return {
      day: isolate(day(from) === day(end) ? day(from) : `${day(from)} - ${day(end)}`),
      hours: isolate(`${time(from)} - ${time(end)}`),
    }
  }

  /**
   * The same two lines as markup, for the columns that can only hand the table a string - a short
   * window, or a longer one whose ends fall mid-day (`timedRangeParts`).
   *
   * The vehicle and HME lists build their range from `time_limitations.range_allowed`, whose epochs
   * were shifted to the client timezone when they were written - so they pass their dates straight to
   * `compactRangeParts`, never through `compactPassRange`, which would shift them a second time.
   * @param from Window start
   * @param to Window end
   * @returns The markup, or null when the window is neither short nor timed
   */
  compactRangeHtml(from: Date, to: Date): string | null {
    const parts = this.compactRangeParts(from, to) ?? this.timedRangeParts(from, to)
    if (!parts) return null
    return `<div class="compact-range"><span>${parts.day}</span><span class="compact-range__hours">${parts.hours}</span></div>`
  }

  /**
   * Is date range overlaps
   * @param e1start First start date
   * @param e1end First end date
   * @param e2start Second start date
   * @param e2end Second end date
   * @returns Is overlaps?
   */
  dateRangeOverlaps(e1start: Date, e1end: Date, e2start: Date, e2end: Date) {
    // Handle null/undefined cases
    if (!e1start || !e2start) {
      return false
    }

    if (!e1end) {
      return (e1start >= e2start && e1start <= e2end) || e2start > e1start
    }

    if (!e2end) {
      return false
    }

    // Two ranges overlap if: start1 < end2 AND start2 < end1
    // This properly handles adjacent ranges (touching endpoints are NOT considered overlapping)
    return e1start < e2end && e2start < e1end
  }

  /**
   * Check if two time ranges are adjacent (touching but not overlapping)
   * @param e1start First start date
   * @param e1end First end date
   * @param e2start Second start date
   * @param e2end Second end date
   * @returns Are ranges adjacent?
   */
  dateRangesAreAdjacent(e1start: Date, e1end: Date, e2start: Date, e2end: Date): boolean {
    if (!e1start || !e1end || !e2start || !e2end) {
      return false
    }

    // Adjacent if end of one equals start of the other
    return e1end.getTime() === e2start.getTime() || e2end.getTime() === e1start.getTime()
  }

  /**
   * Is dates equals in utc?
   * @param startingDate Stating date
   * @param endingDate Ending date
   */
  isDatesEquals(startingDate: string, endingDate: string) {
    const startDate = this.convertToLocalDate(startingDate)
    const endDate = this.convertToLocalDate(endingDate)
    const endDateCheck = new Date(endDate)
    endDateCheck.setDate(endDateCheck.getDate() - 1)
    return startDate.valueOf() == endDateCheck.valueOf()
  }

  /**
   * Get ending date for display state
   * @param startingDate Stating date
   * @param endingDate Ending date
   */
  getEndingDateToDisplay(startingDate: string, endingDate: string) {
    if (this.isDatesEquals(startingDate, endingDate)) {
      return startingDate
    }
    return endingDate
  }

  /**
   * Get time limitation hours
   * @param timeLimitation
   */
  limitationHours(timeLimitation: string) {
    const restriction = this.buildAndGetRestrictionsByTimeLimitation(timeLimitation, 'days_hours_allowed')
    const parseToTooltip: { name: string }[] = []

    restriction.forEach((element) => {
      let val = ''
      element.daysWeek.forEach((el, i: number) => {
        if (i != element.daysWeek?.length - 1) {
          val += el.name + ', '
        } else {
          val += el.name
        }
      })

      if (document.dir == 'ltr') val = `${val} : ${this.datePipe.transform(element.hourFirstRange, 'HH:mm')} - ${this.datePipe.transform(element.hourSecondRange, 'HH:mm')}`
      if (document.dir == 'rtl') val = `${val} : ${this.datePipe.transform(element.hourSecondRange, 'HH:mm')} - ${this.datePipe.transform(element.hourFirstRange, 'HH:mm')}`

      parseToTooltip.push({ name: val })
    })
    return parseToTooltip
  }

  /**
   * Get day index (day of week 1,2,3 ..)
   * @param date
   */
  getDayIndex(date: string) {
    return new Date(this.convertToLocalDate(date)).getDay()
  }

  /**
   * Get day from date
   * @param date
   */
  getDayFromDate(date: string) {
    const daysList = this.commonService.getDayList()
    const day = this.getDayIndex(date)
    return daysList[day].name
  }

  /**
   * Get only date from another date and hours from current
   * @param date
   * @param anotherDate
   */
  buildDateFromAnotherDate(date: Date, anotherDate: Date) {
    const day = new Date(this.convertToLocalDate(date))
    const newDate = new Date(this.convertToLocalDate(anotherDate))
    day.setHours(newDate.getHours(), newDate.getMinutes(), newDate.getSeconds(), newDate.getMilliseconds())
    return day
  }

  /**
   * Covert date from normal string like 23/10/1193
   * @param stringDate
   */
  convertDateFromStringNormal(stringDate: string) {
    const [year, month, day]: string[] = stringDate.split(/[./-]/).filter(Boolean)
    const awesomeDate = `${day}-${month}-${year}`
    const newDate = new Date(awesomeDate)
    return newDate
  }

  /**
   * Convert time from normal string like 12:45
   * @param stringTime
   * @param usedDate
   * @returns
   */
  convertTimeFromStringNormal(stringTime: string, usedDate?: string | Date) {
    const timeDate = usedDate ? new Date(this.convertToLocalDate(usedDate)) : new Date(this.convertToLocalDate(new Date()))
    if (stringTime) {
      if (!stringTime?.includes(':')) stringTime = [stringTime.slice(0, 2), ':', stringTime.slice(2, 4)].join('')

      const [hours, minutes] = stringTime.split(':')
      timeDate.setHours(+hours)
      timeDate.setMinutes(+minutes)
      timeDate.setSeconds(0)
      timeDate.setMilliseconds(0)
    }
    return timeDate
  }

  /**
   * Compare between two dates
   * @param firstDate First date to compare
   * @param secondDate Second date to compare
   * @param operator > < >= <= == !=
   * @returns Is first date [operator] second date
   */
  compareBetweenTwoDates(firstDate: Date | string, secondDate: Date | string, operator: '>' | '<' | '>=' | '<=' | '==' | '!=') {
    if (firstDate && secondDate) {
      const fDate = new Date(this.convertToLocalDate(firstDate))
      const sDate = new Date(this.convertToLocalDate(secondDate))

      switch (operator) {
        case '>':
          return fDate > sDate
        case '<':
          return fDate < sDate
        case '>=':
          return fDate >= sDate
        case '<=':
          return fDate <= sDate
        case '==':
          return fDate == sDate
        case '!=':
          return fDate != sDate
        default:
          return false
      }
    }
    return false
  }

  getConvertedTimeToUTCFrom(date: Date) {
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()

    // Format the hours and minutes as strings with leading zeros if needed
    const hoursString = hours.toString().padStart(2, '0')
    const minutesString = minutes.toString().padStart(2, '0')

    // Create a string representing the UTC hours and minutes
    return `${hoursString}${minutesString}`
  }

  /**
   * Convert the time to UTC and remove :
   * @param element
   */
  timeToUTC(element: Record<string, unknown>, key: string) {
    const date = this.convertTimeFromStringNormal(element[key] as string, new Date())
    element[key] = this.getConvertedTimeToUTCFrom(new Date(date))
  }

  /**
   * Build the pass time limitations range by providing the form group and model data
   * @param form
   * @param model
   * @returns
   */
  buildTimeLimitationRange(form: FormGroup, model: unknown, endingDateCanBeNull?: boolean, startingDateKey: string = 'starting_date', endingDateKey: string = 'ending_date') {
    if (endingDateCanBeNull) {
      if (!form.get(endingDateKey)?.value) {
        const newDateForEnding = new Date(environment.InfiniteDate)
        form.get(endingDateKey)?.setValue(newDateForEnding)
      }
    }

    const data = this.timezoneOffset(form.get(startingDateKey)?.value as string | Date | null, form.get(endingDateKey)?.value as string | Date | null)

    let startingDate: Date = data.startingDate
    let endingDate: Date = data.endingDate

    // Normalize starting date to start at midnight (00:00:00) UTC
    const startingDateObj = new Date(startingDate)
    startingDateObj.setHours(0, 0, 0, 0)
    startingDate = startingDateObj

    const endingDateObj = new Date(endingDate)
    endingDateObj.setHours(24, 0, 0, 0)
    endingDate = endingDateObj

    // NOTE: do NOT write these normalized dates back into the form.
    // Callers (e.g. pass-modal) call this on event selection while the form still
    // holds the user's picked dates; mutating ending_date to next-day 00:00 here,
    // followed by a separate setHours(23,59,59) at save time, double-applied the
    // day rollover and pushed the pass end to the next day. This function must stay
    // pure and only return the computed range.

    return {
      range_allowed: `${Math.floor(startingDate.valueOf() / 1000)}-${Math.floor(endingDate.valueOf() / 1000)}`,
    }
  }

  timezoneOffset(startingDate: string | Date | number | null, endingDate: string | Date | number | null) {
    const startingTimestamp = new Date(startingDate ?? 0).getTime()
    const endingTimestamp = new Date(endingDate ?? 0).getTime()
    // Parse the input date string
    const startingTimeZoneOffset = new Date(startingDate ?? 0).getTimezoneOffset() * 60000
    const endingTimeZoneOffset = new Date(endingDate ?? 0).getTimezoneOffset() * 60000

    startingDate = startingTimestamp - startingTimeZoneOffset
    endingDate = endingTimestamp - endingTimeZoneOffset

    return {
      startingDate: new Date(startingDate),
      endingDate: new Date(endingDate),
    }
  }

  convertToISOWithTimezone(sourceDate: string | Date): string {
    this.initializeTimezone()
    const date = typeof sourceDate === 'string' ? new Date(new Date(this.dateByTimezone(sourceDate, 'yyyy-MM-ddTHH:mm:ss.SSSZ') ?? '').toISOString()) : sourceDate

    const sourceOffset = this.getTimezoneOffset(this.datePipe.transform(new Date(), 'long')!.substring(this.datePipe.transform(new Date(), 'long')!.length - 5))

    const targetOffset = this.getTimezoneOffset(this.timezone)

    date.setMinutes(date.getMinutes() - sourceOffset)
    date.setMinutes(date.getMinutes() + targetOffset)

    return date.toISOString()
  }

  /**
   * YB-2298 — "now" for a date picker: the client's wall-clock time, as the picker (which shows the
   * browser's zone) must hold it. The inverse of {@link pickerToInstant}; both are no-ops when the
   * browser is in the client's zone.
   * @returns Date to put in the picker
   */
  clientNowForPicker(): Date {
    return new Date(this.dateToDisplay(new Date()))
  }

  /**
   * YB-2298 — the real moment a picker value means: the picker holds the client's wall-clock time in
   * the browser's zone (see {@link clientNowForPicker}). Send this, never the picker's Date as is.
   * @param pickerDate Date from the picker
   * @returns The instant, or null for no date
   */
  pickerToInstant(pickerDate: Date | null | undefined): Date | null {
    if (!pickerDate) return null
    return new Date(this.convertToISOWithTimezone(new Date(pickerDate)))
  }

  getTimezoneOffset(timezone: string): number {
    // Use a mapping of timezones and their offsets
    const timezoneOffsets: Record<string, number> = {
      'GMT-12': 720,
      'GMT-11': 660,
      'GMT-10': 600,
      'GMT-9': 540,
      'GMT-8': 480,
      'GMT-7': 420,
      'GMT-6': 360,
      'GMT-5': 300,
      'GMT-4': 240,
      'GMT-3': 180,
      'GMT-2': 120,
      'GMT-1': 60,
      'GMT+0': 0,
      'GMT+1': -60,
      'GMT+2': -120,
      'GMT+3': -180,
      'GMT+4': -240,
      'GMT+5': -300,
      'GMT+6': -360,
      'GMT+7': -420,
      'GMT+8': -480,
      'GMT+9': -540,
      'GMT+10': -600,
      'GMT+11': -660,
      'GMT+12': -720,
    }

    // Retrieve the offset for the specified timezone
    return timezoneOffsets[timezone] || 0
  }

  get isTheCurrentTimezoneIdDifferent() {
    this.initializeTimezone()
    const sourceOffset = this.getTimezoneOffset(this.datePipe.transform(new Date(), 'long')!.substring(this.datePipe.transform(new Date(), 'long')!.length - 5))
    const targetOffset = this.getTimezoneOffset(this.timezone)
    return sourceOffset != targetOffset
  }

  dateByTimezone(date: string | Date | number | null | undefined, otherFormat: string = 'medium') {
    this.initializeTimezone()
    const value = this.datePipe.transform(date, otherFormat, this.timezone)
    return value
  }

  dateToDisplay(sourceDate: string | Date) {
    this.initializeTimezone()
    const date = typeof sourceDate === 'string' ? new Date(new Date(this.dateByTimezone(sourceDate, 'yyyy-MM-ddTHH:mm:ss.SSSZ') ?? '').toISOString()) : sourceDate

    const sourceOffset = this.getTimezoneOffset(this.datePipe.transform(new Date(), 'long')!.substring(this.datePipe.transform(new Date(), 'long')!.length - 5))

    const targetOffset = this.getTimezoneOffset(this.timezone)

    date.setMinutes(date.getMinutes() + sourceOffset)
    date.setMinutes(date.getMinutes() - targetOffset)

    return date.toISOString()
  }

  /**
   * may the ending date not provided?
   * @param endingDate Ending date
   * @returns Is not provided date
   */
  noDateProvided(endingDate: string) {
    const currentDate = this.timezoneOffset(endingDate, null).startingDate
    const withoutEndingDate = this.timezoneOffset(environment.InfiniteDate, null).startingDate

    if (currentDate.getFullYear() == withoutEndingDate.getFullYear() || currentDate.getFullYear() == withoutEndingDate.getFullYear() + 1) {
      return true
    }
    return false
  }
}
